import type { PortableTextLayoutArtifacts, PortableTextLayoutInput } from './portable-text-layout';

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Operation cancelled.', 'AbortError');
}

/** Draft storage contract. Native archive admission/rendering is a separate gate. */
export const PORTABLE_TEXT_SNAPSHOT = Object.freeze({
  format: 'maipo-text-layout-snapshot', version: '0.1-draft',
  orchestration: 'maipo-paragraph-orchestration-0.1-draft',
  maxCodeUnits: 8 * 1024 * 1024,
});
export interface AdmittedPortableTextSnapshot {
  serialized: string;
  layout: PortableTextLayoutArtifacts;
}

export function capturePortableTextSnapshotInput(input: PortableTextLayoutInput): PortableTextLayoutInput {
  if (typeof input.text !== 'string' || input.text.length > 20_000 || Array.from(input.text).length > 10_000) throw new Error('Portable snapshot source exceeds its limit.');
  if (!Array.isArray(input.fonts) || input.fonts.length === 0 || input.fonts.length > 16) throw new Error('Portable snapshot font count exceeds its limit.');
  let total = 0;
  for (const font of input.fonts) {
    if (!(font.fontBytes instanceof Uint8Array) || !(font.fontBytes.buffer instanceof ArrayBuffer) || font.fontBytes.byteLength === 0 || font.fontBytes.byteLength > 64 * 1024 * 1024) throw new Error('Portable snapshot font bytes exceed their limit.');
    total += font.fontBytes.byteLength;
  }
  if (total > 128 * 1024 * 1024) throw new Error('Portable snapshot total font bytes exceed their limit.');
  const { fonts, ...settings } = input;
  const captured = { ...structuredClone(settings), fonts: fonts.map(({ fontBytes, ...font }) => ({ ...structuredClone(font), fontBytes: new Uint8Array(fontBytes) })) };
  // JSON storage has one zero representation. Normalize signed zeros before
  // creating the metric record's binary64 settings so reopen is lossless.
  const number = (value: number) => Object.is(value, -0) ? 0 : value;
  captured.revision = number(captured.revision);
  captured.topLeft = { x: number(captured.topLeft.x), y: number(captured.topLeft.y) };
  captured.lineGap = number(captured.lineGap);
  if (captured.emptyLineFontIndex !== undefined) captured.emptyLineFontIndex = number(captured.emptyLineFontIndex);
  for (const font of captured.fonts) {
    if (font.faceIndex !== undefined) font.faceIndex = number(font.faceIndex);
    for (const variation of font.variations ?? []) variation.value = number(variation.value);
  }
  for (const feature of captured.features ?? []) feature.value = number(feature.value);
  return captured;
}

async function digest(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  return 'sha256:' + [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Internal assembly for trusted worker results; never an admission API. */
export async function snapshotFromFreshPortableLayout(captured: PortableTextLayoutInput, layout: PortableTextLayoutArtifacts, signal?: AbortSignal): Promise<AdmittedPortableTextSnapshot> {
  throwIfAborted(signal);
  const fonts = await Promise.all(captured.fonts.map(async font => ({
    fontRef: font.fontRef, digest: await digest(font.fontBytes as Uint8Array<ArrayBuffer>), faceIndex: font.faceIndex ?? 0,
    variations: (font.variations ?? []).map(({ tag, value }) => ({ tag, value })).sort((a, b) => a.tag < b.tag ? -1 : a.tag > b.tag ? 1 : 0),
  })));
  throwIfAborted(signal);
  const serialized = JSON.stringify({
    format: PORTABLE_TEXT_SNAPSHOT.format, version: PORTABLE_TEXT_SNAPSHOT.version,
    orchestration: PORTABLE_TEXT_SNAPSHOT.orchestration,
    source: { documentId: captured.documentId, revision: captured.revision, nodeId: captured.nodeId, text: captured.text },
    fonts,
    settings: { fontSize: captured.fontSize, width: captured.width, topLeft: { x: captured.topLeft.x, y: captured.topLeft.y },
      metricsPolicy: captured.metricsPolicy, lineGap: captured.lineGap, overflow: captured.overflow,
      emptyLineFontIndex: captured.emptyLineFontIndex ?? null, baseDirection: captured.baseDirection,
      language: captured.language, scriptLanguages: Object.fromEntries(Object.entries(captured.scriptLanguages ?? {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)),
      features: (captured.features ?? []).map(({ tag, value }) => ({ tag, value })).sort((a, b) => a.tag < b.tag ? -1 : a.tag > b.tag ? 1 : 0) },
    layout,
  });
  if (serialized.length > PORTABLE_TEXT_SNAPSHOT.maxCodeUnits) throw new Error('Portable text snapshot exceeds its serialized limit.');
  return { serialized, layout };
}

/** Cheap header preflight; full admission still requires exact recomputation. */
export function assertPortableTextSnapshotHeader(serialized: string): void {
  if (typeof serialized !== 'string' || serialized.length > PORTABLE_TEXT_SNAPSHOT.maxCodeUnits) throw new Error('Portable text snapshot exceeds its serialized limit.');
  let header: unknown;
  try { header = JSON.parse(serialized) as unknown; }
  catch { throw new Error('Portable text snapshot is not valid JSON.'); }
  if (!header || typeof header !== 'object' || Array.isArray(header)
    || !('format' in header) || header.format !== PORTABLE_TEXT_SNAPSHOT.format
    || !('version' in header) || header.version !== PORTABLE_TEXT_SNAPSHOT.version
    || !('orchestration' in header) || header.orchestration !== PORTABLE_TEXT_SNAPSHOT.orchestration) throw new Error('Unsupported portable text snapshot version or policy.');
}
